import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import { ButtonContent, Button } from 'components/Common/MasterComponents'
import { useMemo, useState } from 'react'
import ImagesPopup from 'components/Common/ImagesPopup'
import RecognizedState from './RecognizedState'
import UnrecognizedState from './UnrecognizedState'
import CameraIcon from './CameraIcon'
import { ImageCounter } from './_styles'
import { FIELDS, STEPS } from '../_methods'
import { ActionsBlock, Main } from '../_styles'
import ConfirmDialog from '../Dialogs/ConfirmDialog'
import IncorrectRecognitionDialog from '../Dialogs/IncorrectRecognitionDialog'
import { ProductRecognisePayload } from 'api/schemas'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`
interface RecognitionResultProps {
  onClose: (productRecognise?: ProductRecognisePayload) => void
  changeStep: (step: number) => void
  handleIncorrectRecognition: (comment: string) => void
  handleUpdate: () => void
  isLoading: boolean
  isRecognizeWineMode?: boolean // для случая использовани в RecognizeWine
  testIdPrefix?: string
}

const RecognitionResult = ({
  onClose,
  changeStep,
  handleIncorrectRecognition,
  handleUpdate,
  isLoading,
  isRecognizeWineMode = false,
  testIdPrefix,
}: RecognitionResultProps) => {
  const { t } = useTranslation()
  const { watch, setValue, getValues } = useFormContext()

  const [isFullScreenDialogOpen, setIsFullScreenDialogOpen] = useState(false)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [selectedImages, setSelectedImages] = useState<string[]>([])
  const [isOpenIncorrectDialog, setIsOpenIncorrectDialog] = useState(false)

  const [recognitionData, bottleImages, additionalImages] = watch([
    FIELDS.RECOGNITION_DATA,
    FIELDS.BOTTLE_IMAGES,
    FIELDS.ADDITIONAL_IMAGES,
  ])

  const imagesFromStep1 = useMemo(
    () =>
      [
        bottleImages.frontLabel,
        bottleImages.backLabel,
        bottleImages.bottleLabel,
        additionalImages?.first,
        additionalImages?.second,
        additionalImages?.third,
      ].filter(Boolean),
    [bottleImages, additionalImages]
  )

  const handleReturn = () => {
    setValue(FIELDS.RECOGNITION_DATA, null)
    changeStep(STEPS.uploadImages)
  }

  const toggleFullScreenDialog = (images?: string[]) => {
    if (images) {
      setSelectedImages(images)
    }
    setIsFullScreenDialogOpen(prev => !prev)
  }

  const toggleConfirmDialog = () => {
    setIsConfirmDialogOpen(prev => !prev)
  }

  const toggleIncorrectRecognitionDialog = () => {
    setIsOpenIncorrectDialog(prev => !prev)
  }

  const handleUpdateAndClose = async () => {
    await handleUpdate()
    const currentRecognitionData = getValues(FIELDS.RECOGNITION_DATA)
    onClose(currentRecognitionData)
  }

  const handleIncorrectRecognitionWithToggle = async (comment: string) => {
    await handleIncorrectRecognition(comment)
    toggleIncorrectRecognitionDialog()
  }

  const renderCameraIconCounter = (count: number) => (
    <ImageCounter
      data-testid={
        testIdPrefix ? `${testIdPrefix}-recognition-image-counter` : undefined
      }
    >
      <CameraIcon />
      {count}
    </ImageCounter>
  )

  return (
    <>
      <Main
        data-testid={
          testIdPrefix ? `${testIdPrefix}-recognition-result` : undefined
        }
      >
        <Wrapper>
          {recognitionData?.product ? (
            <RecognizedState
              images={imagesFromStep1}
              product={recognitionData?.product}
              onIncorrectClick={toggleIncorrectRecognitionDialog}
              onImageClick={toggleFullScreenDialog}
              renderImageCounter={renderCameraIconCounter}
              testIdPrefix={testIdPrefix}
            />
          ) : (
            <UnrecognizedState
              images={imagesFromStep1}
              imageCount={imagesFromStep1.length}
              onImageClick={toggleFullScreenDialog}
              renderImageCounter={renderCameraIconCounter}
              testIdPrefix={testIdPrefix}
            />
          )}
        </Wrapper>
      </Main>

      <ActionsBlock
        data-testid={
          testIdPrefix ? `${testIdPrefix}-recognition-actions` : undefined
        }
      >
        <Button
          $size='L'
          $type='secondary'
          onClick={handleReturn}
          data-testid={
            testIdPrefix ? `${testIdPrefix}-reupload-button` : undefined
          }
        >
          {t('capturedWines.dialog.updateImages.reupload')}
        </Button>
        <Button
          $size='L'
          $type='primary'
          $fakeDisabled={isLoading}
          onClick={
            isRecognizeWineMode
              ? handleUpdateAndClose
              : recognitionData.isUsed
              ? toggleConfirmDialog
              : handleUpdateAndClose
          }
          data-testid={
            testIdPrefix ? `${testIdPrefix}-confirm-button` : undefined
          }
        >
          <ButtonContent loading={isLoading}>
            {t('common.confirm')}
          </ButtonContent>
        </Button>
      </ActionsBlock>
      {isFullScreenDialogOpen && (
        <ImagesPopup
          images={selectedImages.map(image => ({ imageUrl: image }))}
          onClose={toggleFullScreenDialog}
        />
      )}
      {isConfirmDialogOpen && (
        <ConfirmDialog
          onUpdate={handleUpdateAndClose}
          onClose={toggleConfirmDialog}
          loading={isLoading}
        />
      )}
      {isOpenIncorrectDialog && (
        <IncorrectRecognitionDialog
          onClose={toggleIncorrectRecognitionDialog}
          onSubmit={handleIncorrectRecognitionWithToggle}
          loading={isLoading}
        />
      )}
    </>
  )
}

export default RecognitionResult
